import { forwardRef, useState, useEffect } from 'react';
import { useSlate } from 'slate-react';
import { Range, Editor } from 'slate';
import { Chrome, type ColorResult } from '@uiw/react-color';
import { useStyle } from 'entities/style';
import { useUser } from '../../../state/contexts/UserContext';
import { rgbaToHex } from 'shared/lib/helper-functions';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useDebouncedCommit } from 'shared/lib/use-debounced-commit';

interface ColorButtonProps {
  icon: string;
  css?: string;
  format?: string;
  text?: string;
}

export const ColorButton = forwardRef<HTMLButtonElement, ColorButtonProps>(({ icon, css }, ref) => {
  const editor = useSlate();
  const { selection } = editor;
  const hasSelectedText = selection && !Range.isCollapsed(selection);

  return (
    <button ref={ref} className={`${css}`} disabled={!hasSelectedText}>
      <i className={`fa-regular fa-${icon}`} />
    </button>
  );
});

interface ColorPickerProps {
  handleChangeCallBack?: (color: string | undefined) => void;
  type?: string;
  defaultValue?: string;
}

export const ColorPicker = forwardRef<HTMLDivElement, ColorPickerProps>(({ handleChangeCallBack, type, defaultValue }, ref) => {
  const { selectedOrganization } = useUser();
  const [color, setColor] = useState(defaultValue);

  const { data: pDocument } = useEditorDocument();
  const styleId = pDocument.content?.design.find((d) => d.type === 'graphical-variables')?.styleId;
  const { data, isLoading } = useStyle({
    organization: selectedOrganization,
    documentRevisionId: pDocument.revisionId,
    styleId,
  });
  const editor = useSlate();

  useEffect(() => {
    if (defaultValue) {
      setColor(defaultValue);
    } else {
      setColor('#FFFFFF');
    }
  }, [defaultValue]);

  const handleChangeComplete = (color: string | undefined) => {
    if (handleChangeCallBack && typeof handleChangeCallBack === 'function') {
      handleChangeCallBack(color);
      return;
    }
    if (color === '') {
      Editor.removeMark(editor, type as string);
    } else {
      Editor.addMark(editor, type as string, color);
    }
  };
  const commitColor = useDebouncedCommit(handleChangeComplete);
  return (
    <div className="color-picker-wrapper" ref={ref}>
      <Chrome
        color={color || '#ffffff'}
        onChange={(color: ColorResult) => {
          const hex = rgbaToHex({ ...color.rgba });
          setColor(hex);
          commitColor(hex);
        }}
        className="color-picker-inner"
      />
      <p style={{ margin: 0 }}>Temafärger: </p>
      {!isLoading && data && (
        <div className="theme-colors-wrapper">
          {Object.keys(COLOR_MAP).map((key) => {
            const themeColors = (data.content ?? {}) as Record<string, string>;
            return (
              <div
                key={key}
                className="theme-color"
                style={{ backgroundColor: themeColors[key] }}
                onClick={() => {
                  setColor(themeColors[key]);
                  handleChangeComplete(themeColors[key]);
                }}
              />
            );
          })}
          <div key="color-unset" className="theme-color theme-unset fa-2x fa-regular fa-ban" onClick={() => handleChangeComplete('')} />
        </div>
      )}
    </div>
  );
});

const COLOR_MAP = {
  xrpPrimaryColor: {
    label: 'graphical-variable:xrpPrimaryColor',
  },
  xrpSecondaryColor: {
    label: 'graphical-variable:xrpSecondaryColor',
  },
  xrpTertiaryColor: {
    label: 'graphical-variable:xrpTertiaryColor',
  },
  xrpQuaternaryColor: {
    label: 'graphical-variable:xrpQuaternaryColor',
  },
  xrpHeadingColor: {
    label: 'graphical-variable:xrpHeadingColor',
  },
};
